// Structure detail options shared across rate structures
export const STRUCTURE_DETAILS = [
  { value: "none", label: "None" },
  { value: "tobacco", label: "Tobacco" },
  { value: "gender", label: "Gender" },
] as const;

// Age Banded structure detail options include Tiered in addition to the base options
export const AGE_BANDED_STRUCTURE_DETAILS = [
  ...STRUCTURE_DETAILS,
  { value: "tiered", label: "Tiered" },
] as const;

// Sentinel upper bound for the open-ended last age band ("and above"); the UI
// substitutes it for the domain's absent/unbounded upper so inputs have a value.
export const MAX_AGE_SENTINEL = 150;

// Prefixes the lowest age band, which has no lower bound of its own: "≤19"
// covers everyone up to and including 19.
export const AGE_BAND_AT_MOST = "≤";

// Number of tiers options
export const TIER_COUNTS = [2, 3, 4, 5, 6] as const;

// Default number of tiers when selecting tiered rate structure
export const DEFAULT_TIER_COUNT = 4;

// Default tiers for each tier count
export const DEFAULT_TIERS_BY_COUNT: Record<number, string[] | undefined> = {
  2: ["employee", "employeeFamily"],
  3: ["employee", "employeePlusOneDependent", "employeeFamily"],
  4: ["employee", "employeeSpouse", "employeeChildren", "employeeFamily"],
  5: [
    "employee",
    "employeeSpouse",
    "employeeChild",
    "employeeChildren",
    "employeeFamily",
  ],
  6: undefined, // No default - analyst chooses
};

// Coverage tier options in order - using backend enum values
// Note: "employeeChild" and "employeePlusOneDependent" are UI-only variants
// that both map to "employeeDependent" backend value
export const COVERAGE_TIERS = [
  { value: "employee", label: "Employee Only" },
  { value: "employeeSpouse", label: "Employee & Spouse" },
  { value: "employeeChildren", label: "Employee & Children" },
  { value: "employeeFamily", label: "Full Family" },
  { value: "employeeChild", label: "Employee & Child" },
  { value: "employeePlusOneDependent", label: "Employee + 1 Dependent" },
  { value: "employee2Dependents", label: "Employee & 2 Children" },
  { value: "spouseOnly", label: "Spouse Only" },
  { value: "childOnly", label: "Child Only" },
  { value: "spouseDependent", label: "Dependents Only" },
] as const;

// Covered benefit type options for Flat Rate unit selection
export const COVERED_BENEFIT_TYPES = [
  { value: "coveredBenefit", label: "Covered Benefit/Insured Volume" },
  { value: "coveredPayroll", label: "Covered Payroll" },
] as const;

// Rate per X options for Flat Rate unit selection
export const RATE_PER_OPTIONS = [
  { value: "per10", label: "Per $10" },
  { value: "per100", label: "Per $100" },
  { value: "per1000", label: "Per $1,000" },
] as const;

// EMP & Spouse Rates Differ options (for age-banded only)
export const EMP_SPOUSE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "na", label: "N/A" },
] as const;

export const DEFAULT_EMP_SPOUSE_RATES_DIFFER = "no";

// Coverage types that support AD&D rates
export const ADND_COVERAGE_TYPES = [
  "basiclifeadnd",
  "voluntarylifeadnd",
] as const;

export const RATE_STRUCTURE = {
  TIERED: "tiered",
  AGE_BANDED: "ageBanded",
  FLAT: "flat",
  PEPM: "pepm",
} as const;

// UI rate structure options
export const RATE_STRUCTURES = [
  { value: RATE_STRUCTURE.TIERED, label: "Tiered" },
  { value: RATE_STRUCTURE.AGE_BANDED, label: "Age Banded" },
  { value: RATE_STRUCTURE.FLAT, label: "Flat" },
  { value: RATE_STRUCTURE.PEPM, label: "PEPM" },
] as const satisfies {
  value: (typeof RATE_STRUCTURE)[keyof typeof RATE_STRUCTURE];
  label: string;
}[];

// Coverage types that support PEPM rate structure
export const PEPM_SUPPORTED_COVERAGE_TYPES = [
  "dental",
  "dental-ppo",
  "dental-hmo",
  "vision",
  "eap",
  "std",
  "ltd",
  "basiclifeadnd",
  "fmla",
  "individualdisabilityinsurance",
] as const;

export const STANDARD_PEPM_COVERAGE_TYPES =
  PEPM_SUPPORTED_COVERAGE_TYPES.filter(
    (coverageType) => coverageType !== "fmla",
  );

// Plan type the rate-schedule copy endpoint reads as a proposal plan. It reads
// any other value, or none, as a benefit plan.
export const PROPOSAL_PLAN_TYPE = "proposal";
